import config from '~/config';
import { io, Socket } from 'socket.io-client';
import { ClientToServerEvents, ServerToClientEvents, StrategyEvent, StrategyEventCallback } from './types';
import { store } from '~/state/store';
import { updateSymbolPrices } from '~/state/reducers/symbolsSlice';
import { News, NewsCallback, NewsDeleted, NewsDeletedCallback } from '~/api-services/NewsService/types';

class StreamingService {
    socket?: Socket<ServerToClientEvents, ClientToServerEvents>;
    unsubQuotesIntervals: { [key: string]: NodeJS.Timer } = {};
    subQuotesIntervals: { [key: string]: NodeJS.Timer } = {};
    lastSessionId: string = '';
    newsCallbacks: Set<NewsCallback> = new Set<NewsCallback>();
    newsDeletedCallbacks: Set<NewsDeletedCallback> = new Set<NewsDeletedCallback>();
    strategyEventCallbacks: Set<StrategyEventCallback> = new Set<StrategyEventCallback>();

    initConnection(sessionId: string) {
        if (!this.socket || sessionId !== this.lastSessionId) {
            this.lastSessionId = sessionId;
            this.socket = io(config.STREAMING_API_BASE_URL, {
                autoConnect: false,
                query: {
                    sessionId,
                },
                path: '/socketserverv4/socket.io',
            });
            this.setupQuotesListener();
            this.setupNewsListener();
            this.setupStrategyListener();
        }
        this.socket.connect();
    }

    setupQuotesListener() {
        if (this.socket && !this.socket.hasListeners('quote')) {
            this.socket.on('quote', (symbol) => {
                store.dispatch(updateSymbolPrices({ [symbol.symbol]: symbol }));
            });
        }
    }

    setupNewsListener() {
        if (!this.socket) {
            return;
        }
        if (!this.socket.hasListeners('news2')) {
            this.socket.on('news2', (n: News) => {
                for (let cb of this.newsCallbacks) {
                    cb(n);
                }
            });
        }
        if (!this.socket.hasListeners('news2Deleted')) {
            this.socket.on('news2Deleted', (n: NewsDeleted) => {
                for (let cb of this.newsDeletedCallbacks) {
                    cb(n);
                }
            });
        }
        this.socket.on('connect', () => {
            this.socket.emit('join', '/news2/en');
            this.socket.emit('join', '/news2Deleted');
        });
    }

    setupStrategyListener() {
        if (!this.socket) {
            return;
        }
        // No join needed: socketserverv4 auto-joins the account's strategies room from the session.
        if (!this.socket.hasListeners('strategyEvent')) {
            this.socket.on('strategyEvent', (e: StrategyEvent) => {
                for (let cb of this.strategyEventCallbacks) {
                    cb(e);
                }
            });
        }
    }

    closeConnection() {
        this.socket?.disconnect();
    }

    subscribeQuotes(symbols: string[]) {
        for (const s of symbols) {
            setTimeout(() => {
                if (this.unsubQuotesIntervals[s]) {
                    clearInterval(this.unsubQuotesIntervals[s]);
                    delete this.unsubQuotesIntervals[s];
                }
                this.socket?.emit('join', `/quotes/real/${s}`);
            }, 100); // join faster
        }
    }

    unsubscribeQuotes(symbols: string[]) {
        for (const s of symbols) {
            if (this.unsubQuotesIntervals[s]) {
                clearInterval(this.unsubQuotesIntervals[s]);
            }
            this.unsubQuotesIntervals[s] = setTimeout(() => {
                this.socket?.emit('leave', `/quotes/real/${s}`);
                delete this.unsubQuotesIntervals[s];
            }, 1000); // leave slower
        }
    }

    subscribeMaxVolumes(symbol: string) {
        this.socket?.emit('join', `/maxVolumes/${symbol}`);
    }

    subscribeProfitLossExchangeRates(accountCurrency: string) {
        this.socket?.emit('join', `/profitLossExchangeRates/${accountCurrency}`);
    }

    subscribeNews(cb: NewsCallback) {
        this.newsCallbacks.add(cb);
    }

    unsubscribeNews(cb: NewsCallback) {
        this.newsCallbacks.delete(cb);
    }

    subscribeNewsDeleted(cb: NewsDeletedCallback) {
        this.newsDeletedCallbacks.add(cb);
    }

    unsubscribeNewsDeleted(cb: NewsDeletedCallback) {
        this.newsDeletedCallbacks.delete(cb);
    }

    subscribeStrategyEvent(cb: StrategyEventCallback) {
        this.strategyEventCallbacks.add(cb);
    }

    unsubscribeStrategyEvent(cb: StrategyEventCallback) {
        this.strategyEventCallbacks.delete(cb);
    }
}

export default new StreamingService();
